import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  FileText, 
  BookOpen, 
  GraduationCap, 
  PenTool, 
  Shield, 
  Search, 
  CheckCircle, 
  ArrowRight, 
  Mail, 
  Phone,
  Award,
  Globe,
  Star,
  MessageCircle
} from "lucide-react";

export default function ContentWritingServices() {
  const whatsappLink = "https://wa.me/916284607500";

  return (
    <div data-testid="content-writing-services-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxhY2FkZW1pYyUyMHdyaXRpbmclMjByZXNlYXJjaCUyMHBhcGVyfGVufDB8fHx8MTc4MTExMDkwMHww&ixlib=rb-4.1.0&q=85" 
            alt="Academic Writing"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-[1px] w-12 bg-[#C5A059]" />
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Professional Academic Support</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-medium text-white mb-6">
              Content Writing <span className="text-[#C5A059]">Services</span>
            </h1>
            <p className="text-slate-300 text-lg mb-8">
              Expert academic writing support for researchers, scholars, and students. We provide comprehensive content writing services across all domains of academic research.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild className="bg-[#C5A059] hover:bg-[#a88a48] text-slate-950 rounded-none px-8 py-6">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 w-5 h-5" /> Contact on WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-none px-8 py-6">
                <a href="tel:+916284607500">Call Us Now</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SCOPUS/WOS/ABDC Highlight Section */}
      <section className="py-16 bg-gradient-to-r from-[#C5A059] via-[#d4b06a] to-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-2 mb-6">
              <Star className="w-5 h-5 text-[#C5A059]" />
              <span className="font-semibold">PREMIUM RESEARCH PUBLICATION SERVICES</span>
              <Star className="w-5 h-5 text-[#C5A059]" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-950 mb-4">
              SCOPUS | WOS | ABDC
            </h2>
            <p className="text-slate-800 text-lg max-w-2xl mx-auto">
              Get your research published in prestigious indexed journals with our expert guidance and support
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-8 text-center shadow-xl" data-testid="scopus-card">
              <div className="w-20 h-20 bg-slate-950 flex items-center justify-center mx-auto mb-4">
                <Globe className="w-10 h-10 text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">SCOPUS</h3>
              <p className="text-slate-600 text-sm mb-4">World's largest abstract and citation database</p>
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                <span className="bg-green-100 text-green-700 px-3 py-1 text-xs font-medium">High Impact</span>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 text-xs font-medium">Peer Reviewed</span>
              </div>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 text-sm font-medium transition-colors">
                <MessageCircle className="w-4 h-4" /> Contact Now
              </a>
            </div>
            
            <div className="bg-white p-8 text-center shadow-xl" data-testid="wos-card">
              <div className="w-20 h-20 bg-slate-950 flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">Web of Science</h3>
              <p className="text-slate-600 text-sm mb-4">Premier citation indexing service</p>
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                <span className="bg-purple-100 text-purple-700 px-3 py-1 text-xs font-medium">SCI/SCIE</span>
                <span className="bg-orange-100 text-orange-700 px-3 py-1 text-xs font-medium">SSCI</span>
              </div>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 text-sm font-medium transition-colors">
                <MessageCircle className="w-4 h-4" /> Contact Now
              </a>
            </div>
            
            <div className="bg-white p-8 text-center shadow-xl" data-testid="abdc-card">
              <div className="w-20 h-20 bg-slate-950 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-10 h-10 text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-2">ABDC Listed</h3>
              <p className="text-slate-600 text-sm mb-4">Australian Business Deans Council Rankings</p>
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                <span className="bg-red-100 text-red-700 px-3 py-1 text-xs font-medium">A* Journals</span>
                <span className="bg-yellow-100 text-yellow-700 px-3 py-1 text-xs font-medium">A/B/C Ranked</span>
              </div>
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 text-sm font-medium transition-colors">
                <MessageCircle className="w-4 h-4" /> Contact Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">Our Writing Services</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive academic writing support tailored to your research needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Research Paper Writing */}
            <Card className="bg-white border border-slate-100 rounded-none hover:shadow-2xl transition-all group overflow-hidden" data-testid="service-research-paper">
              <div className="h-48 bg-slate-100 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxhY2FkZW1pYyUyMHdyaXRpbmclMjByZXNlYXJjaCUyMHBhcGVyfGVufDB8fHx8MTc4MTExMDkwMHww&ixlib=rb-4.1.0&q=85" 
                  alt="Research Paper Writing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-slate-950 flex items-center justify-center mb-6 -mt-14 relative z-10 group-hover:bg-[#C5A059] transition-colors">
                  <FileText className="w-7 h-7 text-[#C5A059] group-hover:text-slate-950 transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Research Paper Writing</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Expert research paper writing services for all academic domains.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Original Research
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Review Articles
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Case Studies
                  </li>
                </ul>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 font-medium transition-colors">
                  <MessageCircle className="w-5 h-5" /> Contact Now
                </a>
              </CardContent>
            </Card>

            {/* Dissertation Writing */}
            <Card className="bg-white border border-slate-100 rounded-none hover:shadow-2xl transition-all group overflow-hidden" data-testid="service-dissertation">
              <div className="h-48 bg-slate-100 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1623461487986-9400110de28e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxkaXNzZXJ0YXRpb24lMjB0aGVzaXMlMjBncmFkdWF0aW9ufGVufDB8fHx8MTc4MTExMDkwMHww&ixlib=rb-4.1.0&q=85" 
                  alt="Dissertation Writing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-slate-950 flex items-center justify-center mb-6 -mt-14 relative z-10 group-hover:bg-[#C5A059] transition-colors">
                  <GraduationCap className="w-7 h-7 text-[#C5A059] group-hover:text-slate-950 transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Dissertation Writing</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Comprehensive dissertation writing support from proposal to final submission.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Proposal Development
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Literature Review
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Data Analysis
                  </li>
                </ul>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 font-medium transition-colors">
                  <MessageCircle className="w-5 h-5" /> Contact Now
                </a>
              </CardContent>
            </Card>

            {/* Thesis Writing */}
            <Card className="bg-white border border-slate-100 rounded-none hover:shadow-2xl transition-all group overflow-hidden" data-testid="service-thesis">
              <div className="h-48 bg-slate-100 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1590012314607-cda9d9b699ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxkaXNzZXJ0YXRpb24lMjB0aGVzaXMlMjBncmFkdWF0aW9ufGVufDB8fHx8MTc4MTExMDkwMHww&ixlib=rb-4.1.0&q=85" 
                  alt="Thesis Writing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-slate-950 flex items-center justify-center mb-6 -mt-14 relative z-10 group-hover:bg-[#C5A059] transition-colors">
                  <BookOpen className="w-7 h-7 text-[#C5A059] group-hover:text-slate-950 transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Thesis Writing</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Professional thesis writing assistance for master's and doctoral students.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> M.Phil Thesis
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Ph.D. Thesis
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Master's Thesis
                  </li>
                </ul>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 font-medium transition-colors">
                  <MessageCircle className="w-5 h-5" /> Contact Now
                </a>
              </CardContent>
            </Card>

            {/* Other Content Writing */}
            <Card className="bg-white border border-slate-100 rounded-none hover:shadow-2xl transition-all group overflow-hidden" data-testid="service-content-writing">
              <div className="h-48 bg-slate-100 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxhY2FkZW1pYyUyMHdyaXRpbmclMjByZXNlYXJjaCUyMHBhcGVyfGVufDB8fHx8MTc4MTExMDkwMHww&ixlib=rb-4.1.0&q=85" 
                  alt="Content Writing"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-slate-950 flex items-center justify-center mb-6 -mt-14 relative z-10 group-hover:bg-[#C5A059] transition-colors">
                  <PenTool className="w-7 h-7 text-[#C5A059] group-hover:text-slate-950 transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Other Content Writing</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Diverse academic content writing services including books and chapters.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Pre-Written Books
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Book Chapters
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Abstracts & Synopses
                  </li>
                </ul>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 font-medium transition-colors">
                  <MessageCircle className="w-5 h-5" /> Contact Now
                </a>
              </CardContent>
            </Card>

            {/* AI and Plag Checking - with Turnitin & Drillbit */}
            <Card className="bg-white border border-slate-100 rounded-none hover:shadow-2xl transition-all group overflow-hidden" data-testid="service-ai-plag-check">
              <div className="h-48 bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
                <div className="flex items-center gap-6">
                  {/* Turnitin Logo */}
                  <div className="bg-white p-3 rounded-lg shadow-lg">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#0b3d91] flex items-center justify-center rounded">
                        <span className="text-white font-bold text-2xl">T</span>
                      </div>
                      <span className="text-[#0b3d91] font-bold text-xs mt-1 block">Turnitin</span>
                    </div>
                  </div>
                  {/* Drillbit Logo */}
                  <div className="bg-white p-3 rounded-lg shadow-lg">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#ff6b00] flex items-center justify-center rounded">
                        <span className="text-white font-bold text-2xl">D</span>
                      </div>
                      <span className="text-[#ff6b00] font-bold text-xs mt-1 block">Drillbit</span>
                    </div>
                  </div>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-slate-950 flex items-center justify-center mb-6 -mt-14 relative z-10 group-hover:bg-[#C5A059] transition-colors">
                  <Search className="w-7 h-7 text-[#C5A059] group-hover:text-slate-950 transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">AI & Plagiarism Checking</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Industry-leading tools including Turnitin & Drillbit for authenticity checks.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Turnitin Reports
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Drillbit Analysis
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> AI Content Detection
                  </li>
                </ul>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 font-medium transition-colors">
                  <MessageCircle className="w-5 h-5" /> Contact Now
                </a>
              </CardContent>
            </Card>

            {/* AI and Plag Removal - with Turnitin & Drillbit */}
            <Card className="bg-white border border-slate-100 rounded-none hover:shadow-2xl transition-all group overflow-hidden" data-testid="service-ai-plag-removal">
              <div className="h-48 bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center p-4">
                <div className="flex items-center gap-6">
                  {/* Turnitin Logo */}
                  <div className="bg-white p-3 rounded-lg shadow-lg">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#0b3d91] flex items-center justify-center rounded">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <span className="text-[#0b3d91] font-bold text-xs mt-1 block">Verified</span>
                    </div>
                  </div>
                  {/* Drillbit Logo */}
                  <div className="bg-white p-3 rounded-lg shadow-lg">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#22c55e] flex items-center justify-center rounded">
                        <CheckCircle className="w-8 h-8 text-white" />
                      </div>
                      <span className="text-[#22c55e] font-bold text-xs mt-1 block">Clean</span>
                    </div>
                  </div>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-slate-950 flex items-center justify-center mb-6 -mt-14 relative z-10 group-hover:bg-[#C5A059] transition-colors">
                  <Shield className="w-7 h-7 text-[#C5A059] group-hover:text-slate-950 transition-colors" />
                </div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">AI & Plagiarism Removal</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Expert rewriting services to ensure your content passes all checks.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> AI Content Humanization
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Paraphrasing
                  </li>
                  <li className="flex items-center gap-2 text-slate-500 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#C5A059]" /> Citation Correction
                  </li>
                </ul>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 font-medium transition-colors">
                  <MessageCircle className="w-5 h-5" /> Contact Now
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Delivery Timelines Section */}
      <section className="py-20 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">Delivery Timelines</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-600 max-w-2xl mx-auto">
              We deliver quality work within committed timelines
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 border-l-4 border-[#C5A059] shadow-md">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Normative Research Paper</h3>
                <span className="bg-[#C5A059] text-slate-950 px-3 py-1 text-sm font-bold">1 Day</span>
              </div>
              <p className="text-slate-500 text-sm">Quick turnaround for standard research papers</p>
            </div>
            
            <div className="bg-white p-6 border-l-4 border-blue-500 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Extensive Research Paper</h3>
                <span className="bg-blue-500 text-white px-3 py-1 text-sm font-bold">5 Days</span>
              </div>
              <p className="text-slate-500 text-sm">In-depth research with comprehensive analysis</p>
            </div>
            
            <div className="bg-white p-6 border-l-4 border-purple-500 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Empirical Research</h3>
                <span className="bg-purple-500 text-white px-3 py-1 text-sm font-bold">30 Days</span>
              </div>
              <p className="text-slate-500 text-sm">Data-driven research with statistical analysis</p>
            </div>
            
            <div className="bg-white p-6 border-l-4 border-emerald-500 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Thesis Writing</h3>
                <span className="bg-emerald-500 text-white px-3 py-1 text-sm font-bold">15 Days</span>
              </div>
              <p className="text-slate-500 text-sm">Complete thesis with all chapters</p>
            </div>
            
            <div className="bg-white p-6 border-l-4 border-orange-500 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Dissertation</h3>
                <span className="bg-orange-500 text-white px-3 py-1 text-sm font-bold">10 Days</span>
              </div>
              <p className="text-slate-500 text-sm">Full dissertation with research methodology</p>
            </div>
            
            <div className="bg-white p-6 border-l-4 border-green-500 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">AI & Plag Check</h3>
                <span className="bg-green-500 text-white px-3 py-1 text-sm font-bold">30 Min</span>
              </div>
              <p className="text-slate-500 text-sm">Quick Turnitin/Drillbit report delivery</p>
            </div>
            
            <div className="bg-white p-6 border-l-4 border-red-500 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">AI & Plag Removal</h3>
                <span className="bg-red-500 text-white px-3 py-1 text-sm font-bold">1 Day</span>
              </div>
              <p className="text-slate-500 text-sm">Content humanization and plagiarism removal</p>
            </div>
          </div>
        </div>
      </section>

      {/* Domains We Cover */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-white mb-6">Domains We Cover</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-400 max-w-2xl mx-auto">
              We provide academic research support across all disciplines
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <DomainTag name="Law & Legal Studies" />
            <DomainTag name="Management" />
            <DomainTag name="Commerce" />
            <DomainTag name="Economics" />
            <DomainTag name="Political Science" />
            <DomainTag name="Sociology" />
            <DomainTag name="Psychology" />
            <DomainTag name="Education" />
            <DomainTag name="Engineering" />
            <DomainTag name="Computer Science" />
            <DomainTag name="Medical Sciences" />
            <DomainTag name="Environmental Studies" />
            <DomainTag name="History" />
            <DomainTag name="Literature" />
            <DomainTag name="Philosophy" />
            <DomainTag name="Finance" />
            <DomainTag name="Marketing" />
            <DomainTag name="HR Management" />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-[1px] w-12 bg-[#C5A059]" />
                <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Why Choose Us</span>
              </div>
              <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">
                Expert Academic Support You Can Trust
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                With a team of experienced academic writers, researchers, and editors, we deliver high-quality content that meets the highest scholarly standards.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Expert writers with domain-specific knowledge</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">100% original, plagiarism-free content</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Timely delivery with revision support</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Confidential and secure process</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700">Affordable pricing with quality assurance</span>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-950 p-8 text-center">
                <div className="font-serif text-5xl text-[#C5A059] font-bold mb-2">500+</div>
                <p className="text-white text-sm">Research Papers</p>
              </div>
              <div className="bg-[#C5A059] p-8 text-center">
                <div className="font-serif text-5xl text-slate-950 font-bold mb-2">100+</div>
                <p className="text-slate-950 text-sm">Dissertations</p>
              </div>
              <div className="bg-[#C5A059] p-8 text-center">
                <div className="font-serif text-5xl text-slate-950 font-bold mb-2">50+</div>
                <p className="text-slate-950 text-sm">SCOPUS Papers</p>
              </div>
              <div className="bg-slate-950 p-8 text-center">
                <div className="font-serif text-5xl text-[#C5A059] font-bold mb-2">18+</div>
                <p className="text-white text-sm">Domains Covered</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-serif text-2xl font-medium text-slate-950 mb-2">Ready to Get Started?</h3>
              <p className="text-slate-800">Contact us today to discuss your academic writing needs.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 flex items-center gap-2 font-medium transition-colors text-lg">
                <MessageCircle className="w-6 h-6" /> WhatsApp Us
              </a>
              <a href="tel:+916284607500" className="bg-slate-950 text-white px-6 py-4 flex items-center gap-2 font-medium hover:bg-slate-800 transition-colors">
                <Phone className="w-5 h-5" /> +91 6284607500
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function DomainTag({ name }) {
  return (
    <div className="bg-slate-900 border border-slate-800 px-4 py-3 text-center hover:border-[#C5A059]/50 transition-colors">
      <span className="text-white text-sm">{name}</span>
    </div>
  );
}
